"use client";

import { cn } from "@ui/lib";
import { Slider } from "@ui/components/slider";
import {
  MODELS,
  type ModelId,
} from "../lib/statistics";

const CONFIDENCE_OPTIONS = [
  { value: 0.8, label: "80%", desc: "Directional / exploratory" },
  { value: 0.9, label: "90%", desc: "Standard for market research" },
  { value: 0.95, label: "95%", desc: "Rigorous / client-facing claims" },
] as const;

interface ConfidenceControlsProps {
  conf: number;
  onConfChange: (v: number) => void;
  rate: number;
  onRateChange: (v: number) => void;
  prompts: number;
  onPromptsChange: (v: number) => void;
  selectedModels: Set<ModelId>;
  onToggleModel: (id: ModelId) => void;
}

export function ConfidenceControls({
  conf,
  onConfChange,
  rate,
  onRateChange,
  prompts,
  onPromptsChange,
  selectedModels,
  onToggleModel,
}: ConfidenceControlsProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 rounded-2xl bg-muted p-6">
      {/* Confidence Level */}
      <div className="space-y-3">
        <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Confidence Level
        </label>
        <div className="flex flex-col gap-2">
          {CONFIDENCE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onConfChange(opt.value)}
              className={cn(
                "flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors",
                conf === opt.value
                  ? "border-primary bg-primary/10"
                  : "border-border hover:border-primary/50",
              )}
            >
              <div
                className={cn(
                  "h-2 w-2 shrink-0 rounded-full",
                  conf === opt.value ? "bg-primary" : "bg-border",
                )}
              />
              <div>
                <div className="font-medium">{opt.label} Confidence</div>
                <div className="text-xs text-muted-foreground">{opt.desc}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Sliders */}
      <div className="space-y-6">
        <div className="space-y-3">
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Observed Mention Rate: {" "}
            <span className="text-primary font-bold normal-case">{rate}%</span>
          </label>
          <div className="text-3xl font-bold text-primary">{rate}%</div>
          <Slider
            value={[rate]}
            onValueChange={([v]) => onRateChange(v)}
            min={5}
            max={95}
            step={5}
          />
          <p className="text-xs text-muted-foreground">
            Worst-case uncertainty is at 50%. Adjust to match your observed data.
          </p>
        </div>

        <div className="space-y-3">
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Prompts per Brand —{" "}
            <span className="text-primary font-bold normal-case">{prompts}</span>
          </label>
          <div className="text-3xl font-bold text-primary">
            {prompts} <span className="text-base font-normal text-muted-foreground">prompts</span>
          </div>
          <Slider
            value={[prompts]}
            onValueChange={([v]) => onPromptsChange(v)}
            min={5}
            max={50}
            step={5}
          />
          <p className="text-xs text-muted-foreground">
            Prompts grouped by persona, buyer stage, JTBD, etc.
          </p>
        </div>
      </div>

      {/* Model Selector */}
      <div className="space-y-3">
        <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          AI Models Tracked: {" "}
          <span className="text-primary font-bold normal-case">
            {selectedModels.size} model{selectedModels.size !== 1 ? "s" : ""}
          </span>
        </label>
        <div className="grid grid-cols-2 gap-2">
          {MODELS.map((m) => {
            const isSelected = selectedModels.has(m.id);
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => onToggleModel(m.id)}
                className={cn(
                  "flex items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-xs transition-colors",
                  isSelected
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary/50",
                )}
              >
                <div
                  className={cn(
                    "h-2 w-2 shrink-0 rounded-sm",
                    isSelected ? "bg-primary" : "bg-border",
                  )}
                />
                {m.label}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">
          Each model is tracked independently. Selecting more multiplies total
          run count and manual effort.
        </p>
      </div>
    </div>
  );
}
